import React, { useState } from 'react';

import { Checkbox, Select, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { Field } from 'client/blocks/form/Field/Field';
import { withForm } from 'client/blocks/form/formHook';
import * as keyset from 'i18n/AliceExternalAppForm';

import styles from './AliceExternalAppEditPage.module.scss';
import { ExternalFormState, SUPPORTED_RESOURCE_MIME_TYPES } from './AliceExternalAppForm.types';
import { CardHeader } from './CardHeader';
import { DomainListField } from './DomainListField';
import { ProcessingStatusBadge } from './ProcessingStatusBadge';
import { SizeInput } from './SizeInput';

const t = i18n(keyset);

/**
 * Per-resource card: uri/mimeType/name/description/meta (prefersBorder/domain/CSP/preferredSize)
 * + read-only internalUri + processing-status badge. The collapse state is local.
 *
 * Built via `withForm` so the parent passes a typed `form` reference — no `any` casts needed
 * for nested-field paths.
 */
export const ResourceCard = withForm({
    defaultValues: {} as ExternalFormState,
    props: {
        index: 0,
        disabled: false,
        onRemove: () => {},
    },
    render: function ResourceCardRender({ form, index, disabled, onRemove }) {
        const [expanded, setExpanded] = useState(true);
        const toggle = () => setExpanded((v) => !v);

        return (
            <div className={styles.card}>
                <CardHeader
                    expanded={expanded}
                    onToggle={toggle}
                    onRemove={onRemove}
                    removeDisabled={disabled}
                    title={
                        <form.Subscribe selector={(s) => s.values.resources[index]?.uri ?? ''}>
                            {(uri) => <span>{uri || `resource[${index}]`}</span>}
                        </form.Subscribe>
                    }
                />
                {expanded && (
                    <div className={styles.cardBody}>
                        <form.Field name={`resources[${index}].uri`}>
                            {(field) => (
                                <Field
                                    required
                                    fieldTitle={t('resource-field-uri')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <TextInput
                                        value={field.state.value}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                        size="m"
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`resources[${index}].mimeType`}>
                            {(field) => (
                                <Field
                                    required
                                    fieldTitle={t('resource-field-mime-type')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <Select
                                        value={[field.state.value]}
                                        onUpdate={(v) => field.handleChange(v[0])}
                                        disabled={disabled}
                                        size="m"
                                        options={SUPPORTED_RESOURCE_MIME_TYPES.map((mime) => ({
                                            value: mime,
                                            content: mime,
                                        }))}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`resources[${index}].name`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('resource-field-name')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <TextInput
                                        value={field.state.value}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                        size="m"
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`resources[${index}].description`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('resource-field-description')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <TextInput
                                        value={field.state.value}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                        size="m"
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`resources[${index}].meta.prefersBorder`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('resource-meta-prefers-border')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <Checkbox
                                        checked={Boolean(field.state.value)}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                        disabled={disabled}
                                    >
                                        {t('resource-meta-prefers-border-label')}
                                    </Checkbox>
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`resources[${index}].meta.domain`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('resource-meta-domain')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <TextInput
                                        value={field.state.value}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                        size="m"
                                        placeholder={t('resource-meta-domain-placeholder')}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <DomainListField
                            form={form}
                            name={`resources[${index}].meta.cspConnectDomains`}
                            title={t('resource-meta-csp-connect')}
                            addLabel={t('resource-meta-csp-add')}
                            disabled={disabled}
                        />
                        <DomainListField
                            form={form}
                            name={`resources[${index}].meta.cspResourceDomains`}
                            title={t('resource-meta-csp-resource')}
                            addLabel={t('resource-meta-csp-add')}
                            disabled={disabled}
                        />

                        <Field
                            fieldTitle={t('resource-meta-preferred-size')}
                            containerClassName={styles.compactField}
                            size="m"
                        >
                            <div className={styles.preferredSizeGrid}>
                                <SizeInput
                                    form={form}
                                    name={`resources[${index}].meta.minWidth`}
                                    placeholder={t('resource-meta-min-width')}
                                    disabled={disabled}
                                />
                                <SizeInput
                                    form={form}
                                    name={`resources[${index}].meta.maxWidth`}
                                    placeholder={t('resource-meta-max-width')}
                                    disabled={disabled}
                                />
                                <SizeInput
                                    form={form}
                                    name={`resources[${index}].meta.minHeight`}
                                    placeholder={t('resource-meta-min-height')}
                                    disabled={disabled}
                                />
                                <SizeInput
                                    form={form}
                                    name={`resources[${index}].meta.maxHeight`}
                                    placeholder={t('resource-meta-max-height')}
                                    disabled={disabled}
                                />
                            </div>
                        </Field>

                        <form.Field name={`resources[${index}].internalUri`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('resource-field-internal-uri')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <TextInput value={field.state.value} disabled size="m" />
                                </Field>
                            )}
                        </form.Field>

                        <form.Subscribe selector={(s) => s.values.resources[index]}>
                            {(r) =>
                                r?.processingStatus ? (
                                    <div className={styles.compactField}>
                                        <ProcessingStatusBadge status={r.processingStatus} error={r.processingError} />
                                    </div>
                                ) : null
                            }
                        </form.Subscribe>
                    </div>
                )}
            </div>
        );
    },
});
